import { useEffect, useRef, ReactNode } from "react";

export interface ResponsiveDataTableProps {
  /**
   * If true, the Bootstrap's responsive table styling will be applied using the `table-responsive` class. Defaults to true.
   * See: https://getbootstrap.com/docs/5.3/content/tables/#responsive-tables
   */
  responsive?: boolean;

  /**
   * If true, the table header will be sticky when scrolling. Defaults to true.
   *
   * In addition, this will dynamically set a CSS variable `--table-header-height` to the height of the table header,
   * which allows scrolling to a table row without the header covering the target content. See `_tables.scss` for how
   * this CSS variable is used to achieve this.
   */
  stickyHeader?: boolean;

  className?: string;
  children: ReactNode;
}

/**
 * A wrapper around a `<table>` element. See the props for details on what additional functionality this wrapper adds to
 * the table.
 */
export function TableWrapper({
  responsive = true,
  stickyHeader = true,
  className = "",
  children,
}: ResponsiveDataTableProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Optionally monitor the height of the table header and save it in the `--table-header-height` CSS variable
  useEffect(() => {
    // Do nothing if this table does not have a sticky header
    if (!stickyHeader) return;

    // Do nothing if the table is not rendered yet
    const wrapper = containerRef.current;
    if (!wrapper) return;
    const header = wrapper.querySelector<HTMLElement>("table > thead");
    if (!header) return;

    // Define a callback to update the header height property
    const setHeaderHeight = () => {
      const height = header.getBoundingClientRect().height;
      wrapper.style.setProperty("--table-header-height", height.toFixed(3) + "px");
    };

    // Set up a ResizeObserver to update the header height any time the header is resized
    const resizeObserver = new ResizeObserver(setHeaderHeight);
    resizeObserver.observe(header);

    // Set the initial header height when the component mounts
    setHeaderHeight();

    // Clean up the ResizeObserver when the component unmounts
    return resizeObserver.disconnect.bind(resizeObserver);
  }, [stickyHeader, children]);

  // Dynamically build the className for the container
  if (responsive) className += " table-responsive";
  if (stickyHeader) className += " sticky-table-container";

  return (
    <div ref={containerRef} className={className}>
      {children}
    </div>
  );
}
